import * as API from '@/modules/matrix/api/matrixApi'
import { Commit } from 'vuex'
import { useMatrixSession } from '@/modules/matrix/bridges/useMatrixSession'
import { resolveStickyTab } from '@/modules/matrix/store/stickyTab'
import { isStaleMatrixType } from '@/modules/matrix/store/staleGuard'
import { isAbortError, takeAbortSignal } from '@/modules/matrix/store/requestAbort'
import type { MatrixStoreDependencies } from '../../api/matrixClient'

export function createBoosters({ client: API, session: useMatrixSession, signal: takeAbortSignal }: MatrixStoreDependencies) {
return {
  namespaced: true,
  state() {
    return {
      boostersPending: [],
      boostersExposed: [],
      pageIdBooster: 1,
      levelID: 0 as number,
      activeLittleTabID: null,
    }
  },
  actions: {
    async getPendingBoosters(
      { commit, rootState, state }: { commit: Commit; rootState: any, state: any },
      { changeTab = false, filter }: any = {},
    ) {
      state.levelID = filter
      const matrixType = rootState.newTypeMatrix ? rootState.newTypeMatrix : rootState.selectedType?.type

      if (matrixType) {
        const matrixFilterUserId = (await useMatrixSession().ensureUserId()) ?? 0
        return API.filterOfActivatedMatrix({
          matrixType,
          matrixFilterUserId,
          matrixFilterPageId: state.pageIdBooster,
          filter: { pending: 1, is_booster: true, level: filter },
        }, { signal: takeAbortSignal('boosters:pending') }).then(response => {
          // Быстрое листание уровней: устаревший ответ списки не трогает
          if (isStaleMatrixType(rootState, matrixType)) return
          // «Липкая» саб-вкладка: остаёмся в текущей, если в ней есть элементы;
          // на соседнюю уходим, только когда текущая пуста, а соседняя — нет.
          if (rootState.bigTabID === 2 && changeTab) {
            const pendingCount = response.data?.totalCount ?? 0
            const exposedCount = state.boostersExposed?.totalCount ?? 0
            const current = rootState.littleTabID === 4 ? 4 : 3
            const tab = resolveStickyTab(current, { 3: pendingCount, 4: exposedCount })
            commit('CHANGE_LITTLE_TAB', tab, { root: true })
            commit('SET_ACTIVE_LITTLE_TAB', tab)
          }

          commit('SET_PENDING_BOOSTERS', response.data)
          commit('partners/SET_COUNT_PENDING_BOOSTERS', response.data?.totalCount, { root: true })
        }).catch(error => {
          if (!isAbortError(error)) throw error // отменённый запрос — не ошибка
        })
      }
    },
    async getExposedBoosters(
      { commit, rootState, state }: { commit: Commit; rootState: any, state: any },
      { changeTab = false, filter }: any = {},
    ) {
      state.levelID = filter

      const matrixType = rootState.newTypeMatrix ? rootState.newTypeMatrix : rootState.selectedType?.type
      if (matrixType) {
        const matrixFilterUserId = (await useMatrixSession().ensureUserId()) ?? 0
        return API.filterOfActivatedMatrix({
          matrixType,
          matrixFilterUserId,
          matrixFilterPageId: state.pageIdBooster,
          filter: { is_booster: true, level: filter },
        }, { signal: takeAbortSignal('boosters:exposed') }).then(response => {
          // Быстрое листание уровней: устаревший ответ списки не трогает
          if (isStaleMatrixType(rootState, matrixType)) return
          // «Липкая» саб-вкладка: остаёмся в текущей, если в ней есть элементы;
          // на соседнюю уходим, только когда текущая пуста, а соседняя — нет.
          if (rootState.bigTabID === 2 && changeTab) {
            const pendingCount = state.boostersPending?.totalCount ?? 0
            const exposedCount = response.data?.totalCount ?? 0
            const current = rootState.littleTabID === 4 ? 4 : 3
            const tab = resolveStickyTab(current, { 3: pendingCount, 4: exposedCount })
            commit('CHANGE_LITTLE_TAB', tab, { root: true })
            commit('SET_ACTIVE_LITTLE_TAB', tab)
          }

          commit('SET_EXPOSED_BOOSTERS', response.data)
        }).catch(error => {
          if (!isAbortError(error)) throw error // отменённый запрос — не ошибка
        })
      }
    },
  },
  mutations: {
    SET_PENDING_BOOSTERS(state: any, boostersPending: any) {
      state.boostersPending = boostersPending
    },
    SET_EXPOSED_BOOSTERS(state: any, boostersExposed: any) {
      state.boostersExposed = boostersExposed
    },
    SET_PAGE_ID_BOOSTERS(state: any, pageIdBooster: any) {
      state.pageIdBooster = pageIdBooster
    },
    SET_ACTIVE_LITTLE_TAB(state: any, id: number) {
      state.activeLittleTabID = id
    },
  },
}
}

export default createBoosters({ client: API, session: useMatrixSession, signal: takeAbortSignal })
